import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Video, Plus, Copy, ExternalLink, Loader2, Settings2, ChevronRight } from "lucide-react";
import ScheduleMeetingDialog from "./ScheduleMeetingDialog";
import { formatMeetingWindow } from "../../lib/meetingTime";

/**
 * This program's client meetings, with the one-step booking flow attached.
 * Lives on the program's Communications tab so scheduling a call sits next to
 * everything else that goes out to the client.
 */
export default function ProgramMeetingsPanel({ programId, programName }) {
  const [meetings, setMeetings] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/meetings", {
        params: { scope: "all", program_id: programId },
      });
      setMeetings(res.data?.items || []);
    } catch {
      setMeetings([]);
    } finally {
      setLoading(false);
    }
  }, [programId]);

  const loadIntegrations = useCallback(async () => {
    try {
      const res = await apiClient.get("/meetings/integrations");
      setIntegrations(res.data?.items || []);
    } catch {
      setIntegrations([]);
    }
  }, []);

  useEffect(() => {
    load();
    loadIntegrations();
  }, [load, loadIntegrations]);

  const now = Date.now();
  const upcoming = meetings.filter((m) => new Date(m.starts_at).getTime() >= now);
  const past = meetings.filter((m) => new Date(m.starts_at).getTime() < now);

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  return (
    <Card className="border border-border" data-testid="program-meetings-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Video className="w-5 h-5 text-muted-foreground" />
            <CardTitle className="text-lg">Meetings</CardTitle>
            {upcoming.length > 0 && (
              <Badge variant="secondary" className="rounded-full">
                {upcoming.length} upcoming
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              asChild
              className="text-muted-foreground"
              title="Connect Zoom or Outlook"
            >
              <Link to="/meetings?tab=setup">
                <Settings2 className="w-3.5 h-3.5" />
              </Link>
            </Button>
            <Button size="sm" className="gap-2" onClick={() => setOpen(true)} data-testid="program-book-meeting">
              <Plus className="w-4 h-4" />
              Book a meeting
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No meetings booked for this project yet. Booking one creates the Zoom link and emails
            the client the invite in a single step.
          </p>
        ) : (
          <div className="space-y-2">
            {[...upcoming, ...past.slice(0, 3)].map((m) => {
              const isPast = new Date(m.starts_at).getTime() < now;
              const cancelled = m.status === "cancelled";
              return (
                <div
                  key={m.id}
                  className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-md border border-border ${
                    isPast || cancelled ? "opacity-60" : ""
                  }`}
                  data-testid="program-meeting-row"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-foreground truncate">{m.title}</span>
                      {cancelled && (
                        <span className="text-[10px] uppercase tracking-[1.5px] text-red-700">
                          Cancelled
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {formatMeetingWindow(m.starts_at, m.duration_minutes, m.timezone, {
                        withYear: true,
                      })}
                      {m.attendees?.length ? ` · ${m.attendees.length} invited` : ""}
                    </p>
                  </div>
                  {m.join_url && !cancelled && (
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => copyLink(m.join_url)}>
                        <Copy className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <a href={m.start_url || m.join_url} target="_blank" rel="noreferrer">
                          <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                          {isPast ? "Open" : "Join"}
                        </a>
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
            <Link
              to="/meetings"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground pt-1"
            >
              All meetings
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        )}
      </CardContent>

      <ScheduleMeetingDialog
        open={open}
        onOpenChange={setOpen}
        programId={programId}
        programName={programName}
        integrations={integrations}
        onIntegrationsChanged={loadIntegrations}
        onScheduled={load}
      />
    </Card>
  );
}
